import { useFormAPI } from "@/widgets/classic-form/form";
import type { FormValues } from "@/widgets/classic-form/form";
import { yearRangePubSub } from "@/widgets/classic-form/form/yearRangePubSub";
import type { PrefixObjectAccessor } from "@/shared/forms";
import { useSubscribe } from "@/shared/utils/useSubscribe";

interface Params {
    matchAllTermsExactly: boolean;
    matchAllTermsSupported: boolean;
}

/**
 * Subscribes to year-range mode changes and reconciles MAT proximity.
 *
 * Reconciler rule (range → single):
 *   if mode === "single"
 *      && dateProximity.year === null
 *      && matchAllTermsExactly === true:
 *          set dateProximity to 0 (exact)
 *   otherwise:
 *          leave dateProximity unchanged
 */
export const useOnYearRangeChange = ({ matchAllTermsExactly, matchAllTermsSupported }: Params) => {
    const { getFieldValue, setFieldValue } = useFormAPI();

    useSubscribe(yearRangePubSub, (fieldPath, mode) => {
        // Only reconcile on range → single transitions
        if (mode === "range") return;

        // Only act when MAT is supported and currently on
        if (!matchAllTermsSupported || !matchAllTermsExactly) return;

        const proximityYearPath = `${fieldPath}.dateProximity.year` as PrefixObjectAccessor<FormValues, []>;
        const proximityMonthPath = `${fieldPath}.dateProximity.month` as PrefixObjectAccessor<FormValues, []>;
        const proximityDayPath = `${fieldPath}.dateProximity.day` as PrefixObjectAccessor<FormValues, []>;

        // Only act when proximity is null (range-origin case)
        const currentProximity = getFieldValue(proximityYearPath) as number | null;
        if (currentProximity !== null) return;

        // Set proximity to exact so MAT stays consistent
        setFieldValue(proximityYearPath, 0);
        setFieldValue(proximityMonthPath, 0);
        setFieldValue(proximityDayPath, 0);
    });
};
